import { useState } from "react"
import { Loader2, Search, Check, AlertCircle } from "lucide-react"
import { validateAbn, formatAbn, stripAbn } from "@shared/utils/abn"
import { contactsApi, type AbnLookupResult } from "@/lib/api"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"

type Props = {
  value: string
  onChange: (value: string) => void
  onLookupSuccess?: (result: Extract<AbnLookupResult, { ok: true }>) => void
  disabled?: boolean
}

export function AbnField({ value, onChange, onLookupSuccess, disabled }: Props) {
  const [isLooking, setIsLooking] = useState(false)
  const [result, setResult] = useState<AbnLookupResult | null>(null)

  const digits = stripAbn(value)
  const isValidLength = digits.length === 11
  const isChecksumValid = isValidLength && validateAbn(digits)

  const handleChange = (v: string) => {
    // Allow only digits and spaces, cap at 14 chars
    const cleaned = v.replace(/[^\d ]/g, "").slice(0, 14)
    onChange(cleaned)
    // Reset result on change
    if (result) setResult(null)
  }

  const handleBlur = () => {
    // Reformat as XX XXX XXX XXX if 11 digits
    const stripped = stripAbn(value)
    if (stripped.length === 11) {
      onChange(formatAbn(stripped))
    }
  }

  const lookup = async () => {
    if (!isChecksumValid) return
    setIsLooking(true)
    setResult(null)
    try {
      const res = await contactsApi.abnLookup(digits)
      setResult(res)
      if (res.ok && onLookupSuccess) onLookupSuccess(res)
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : "Network error" })
    } finally {
      setIsLooking(false)
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="abn">ABN</Label>
      <div className="flex gap-2">
        <Input
          id="abn"
          type="text"
          inputMode="numeric"
          placeholder="XX XXX XXX XXX"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          className="font-mono"
        />
        <Button
          type="button"
          variant="outline"
          onClick={lookup}
          disabled={!isChecksumValid || isLooking || disabled}
          title={isValidLength && !isChecksumValid ? "Invalid ABN checksum" : "Look up on ABR"}
        >
          {isLooking ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Lookup
        </Button>
      </div>

      {isValidLength && !isChecksumValid && (
        <p className="text-destructive flex items-center gap-1.5 text-xs">
          <AlertCircle className="h-3.5 w-3.5" />
          Invalid ABN checksum
        </p>
      )}

      {result?.ok && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <div className="flex items-center gap-1.5 font-medium">
            <Check className="h-4 w-4" />
            ABN verified — {result.businessName}
          </div>
          {(result.entityType || result.state) && (
            <div className="mt-1 text-xs text-emerald-700">
              {[result.entityType, result.state].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      )}

      {result && !result.ok && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
          <div className="flex items-center gap-1.5 font-medium">
            <AlertCircle className="h-4 w-4" />
            {result.error}
          </div>
        </div>
      )}
    </div>
  )
}
